/**
 * Chat Store
 * Manages chat history, streaming state, and session management
 * Persists session data via backend API (not localStorage)
 * 
 * Requirements: 6.7, 6.8
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage, ChatResponseKind, ChatSession, SourceItem } from '../types/api';
import * as chatApi from '../api/chat';

interface ChatState {
  // Session management
  currentSessionId: string | null;
  sessions: ChatSession[];
  isLoadingSessions: boolean;
  isLoadingHistory: boolean;
  
  // Messages
  messages: ChatMessage[];
  isStreaming: boolean;
  currentStreamingMessage: string;
  currentStreamingResponseKind: ChatResponseKind;

  // Session actions
  setCurrentSession: (sessionId: string) => Promise<void>;
  loadChatHistory: (sessionId?: string) => Promise<void>;
  loadSessions: () => Promise<void>;
  createNewSession: () => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  clearCurrentSession: () => Promise<void>;
  
  // Message actions
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string, sources?: SourceItem[]) => void;
  appendStreamingToken: (token: string) => void;
  startStreaming: (responseKind?: ChatResponseKind) => void;
  finishStreaming: (fullResponse: string, responseKind?: ChatResponseKind, sources?: SourceItem[]) => void;
  clearMessages: () => void;
  getHistory: () => Array<{ role: 'user' | 'assistant'; content: string }>;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSessionId: null,
      sessions: [],
      isLoadingSessions: false,
      isLoadingHistory: false,
      messages: [],
      isStreaming: false,
      currentStreamingMessage: '',
      currentStreamingResponseKind: 'chat',

      // Load all chat sessions
      loadSessions: async () => {
        set({ isLoadingSessions: true });
        try {
          const response = await chatApi.listChatSessions();
          set({
            sessions: response.sessions,
            isLoadingSessions: false,
          });
          
          // If no current session, set to active session
          if (!get().currentSessionId && response.active_session_id) {
            await get().setCurrentSession(response.active_session_id);
          }
        } catch (error) {
          console.error('Failed to load chat sessions:', error);
          set({ isLoadingSessions: false });
        }
      },

      // Load chat history for a session
      loadChatHistory: async (sessionId?: string) => {
        set({ isLoadingHistory: true });
        try {
          const targetSessionId = sessionId || get().currentSessionId;
          const response = await chatApi.getChatHistory(targetSessionId || undefined);
          
          // Convert backend messages to frontend format
          // Note: message_json is already parsed by backend
          const messages: ChatMessage[] = response.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.created_at,
            sources: msg.message_json?.sources,
            responseKind: msg.message_json?.responseKind || msg.message_json?.response_kind || 'chat',
            diagnostics: msg.message_json?.diagnostics,
          }));
          
          set({
            messages,
            currentSessionId: response.session.id,
            isLoadingHistory: false,
          });
          
          // Update sessions list with current session
          const sessions = get().sessions;
          const sessionIndex = sessions.findIndex((s) => s.id === response.session.id);
          if (sessionIndex >= 0) {
            sessions[sessionIndex] = response.session;
            set({ sessions: [...sessions] });
          } else {
            set({ sessions: [response.session, ...sessions] });
          }
        } catch (error) {
          console.error('Failed to load chat history:', error);
          set({ isLoadingHistory: false });
        }
      },

      // Switch to a different session
      setCurrentSession: async (sessionId: string) => {
        console.log('Switching to session:', sessionId, 'from:', get().currentSessionId);
        
        if (sessionId === get().currentSessionId) {
          console.log('Already on this session, skipping');
          return;
        }
        
        set({
          currentSessionId: sessionId,
          messages: [],
          currentStreamingMessage: '',
          isStreaming: false,
          currentStreamingResponseKind: 'chat',
        });
        
        console.log('Loading history for session:', sessionId);
        await get().loadChatHistory(sessionId);
        console.log('History loaded, message count:', get().messages.length);
      },

      // Create a new chat session
      createNewSession: async () => {
        try {
          const response = await chatApi.createChatSession();
          const newSession = response.session;
          
          set({
            currentSessionId: newSession.id,
            sessions: [newSession, ...get().sessions],
            messages: [],
            currentStreamingMessage: '',
            isStreaming: false,
            currentStreamingResponseKind: 'chat',
          });
        } catch (error) {
          console.error('Failed to create new session:', error);
        }
      },

      // Delete a chat session
      deleteSession: async (sessionId: string) => {
        try {
          console.log('Deleting session:', sessionId);
          const response = await chatApi.deleteChatSession(sessionId);
          console.log('Delete response:', response);
          
          // Remove from sessions list
          const sessions = get().sessions.filter((s) => s.id !== sessionId);
          set({ sessions });
          
          // If deleted current session, switch to replacement or create new
          if (sessionId === get().currentSessionId) {
            if (response.replacement_session) {
              console.log('Switching to replacement session:', response.replacement_session.id);
              await get().setCurrentSession(response.replacement_session.id);
            } else {
              console.log('No replacement session, creating new one');
              await get().createNewSession();
            }
          }
        } catch (error) {
          console.error('Failed to delete session:', error);
          // Show error to user
          alert(`Failed to delete session: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      },

      // Clear messages in current session
      clearCurrentSession: async () => {
        const sessionId = get().currentSessionId;
        if (!sessionId) return;
        
        try {
          const response = await chatApi.clearChatMessages(sessionId);
          
          set({
            messages: [],
            currentStreamingMessage: '',
            isStreaming: false,
            currentStreamingResponseKind: 'chat',
          });
          
          // Update session in list
          const sessions = get().sessions;
          const sessionIndex = sessions.findIndex((s) => s.id === sessionId);
          if (sessionIndex >= 0) {
            sessions[sessionIndex] = response.session;
            set({ sessions: [...sessions] });
          }
        } catch (error) {
          console.error('Failed to clear session:', error);
        }
      },

      // Add user message to chat history
      addUserMessage: (content) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              role: 'user',
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      // Add assistant message to chat history with optional sources
      addAssistantMessage: (content, sources) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              role: 'assistant',
              content,
              timestamp: new Date().toISOString(),
              sources,
              responseKind: 'chat',
            },
          ],
        })),

      // Append streaming token to current message
      appendStreamingToken: (token) =>
        set((state) => ({
          currentStreamingMessage: state.currentStreamingMessage + token,
        })),

      // Start streaming (reset accumulator)
      startStreaming: (responseKind = 'chat') =>
        set({
          isStreaming: true,
          currentStreamingMessage: '',
          currentStreamingResponseKind: responseKind,
        }),

      // Finish streaming and add complete message to history
      finishStreaming: (fullResponse, responseKind, sources) =>
        set((state) => ({
          isStreaming: false,
          currentStreamingMessage: '',
          currentStreamingResponseKind: 'chat',
          messages: [
            ...state.messages,
            {
              role: 'assistant',
              content: fullResponse,
              timestamp: new Date().toISOString(),
              responseKind: responseKind || state.currentStreamingResponseKind || 'chat',
              sources: sources, // Include sources in the message
            },
          ],
        })),

      // Clear all messages (local only, use clearCurrentSession for backend)
      clearMessages: () =>
        set({
          messages: [],
          currentStreamingMessage: '',
          isStreaming: false,
          currentStreamingResponseKind: 'chat',
        }),

      // Get last 20 messages (10 turns) for backend API
      getHistory: () => {
        const messages = get().messages;
        // Return last 10 turns (20 messages)
        return messages.slice(-20).map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));
      },
    }),
    {
      name: 'second-brain-chat',
      // Only persist currentSessionId, not messages (those come from backend)
      partialize: (state) => ({
        currentSessionId: state.currentSessionId,
      }),
    }
  )
);
